import Immutable from 'immutable';

import type ErrorData from 'sharedreact/data/ErrorData';
import type Filter from 'sharedreact/data/Filter';
import GroupAndCount from 'sharedreact/data/GroupAndCount';
import type SortOrder from 'sharedreact/data/SortOrder';
import type { RangeType } from 'sharedreact/stores/generic/Type';
import ConnectionSupport from 'sharedreact/utils/ConnectionSupport';
import FilterSupport from 'sharedreact/utils/FilterSupport';

import Token from 'workspaces/launcher/data/Token';

type OnFetchSuccess = (
  range: RangeType,
  totalCount: number,
  items: Immutable.List<Token>,
  countPerGroup: Immutable.List<GroupAndCount>,
  filter: Filter,
  advancedFilter: Filter,
  groupBy: SortOrder,
  sortOrder: SortOrder
) => void;

type OnGenerateNewTokenSuccess = (
  status: string,
  tokenString: string
) => void;

type OnRevokeTokensSuccess = (
  ids: Immutable.List<string>
) => void;

type OnGetSettingsSuccess = (
  settings: Immutable.Map<string, any>
) => void;

type OnUpdateTokenSuccess = (
  nrTags: string
) => void;

type OnError = (error: ErrorData) => void;

const _filterKeywords = [ 'user', 'creationdate', 'grantedby', 'tags' ];

function fetch (
  range: RangeType,
  filter: Filter,
  advancedFilter: Filter,
  groupBy: SortOrder,
  sortOrder: SortOrder,
  onSuccess: OnFetchSuccess,
  onError: OnError
): void {
  const expandedFilter: Filter = FilterSupport.expand(
    filter,
    _filterKeywords
  );

  const expandedAdvancedFilter: Filter = FilterSupport.expand(
    advancedFilter,
    _filterKeywords
  );

  ConnectionSupport.post(
    'getTokens',
    {
      range: range,
      filter: expandedFilter,
      advancedFilter: expandedAdvancedFilter,
      groupBy: groupBy,
      sortOrder: sortOrder
    },
    (data) =>
      onSuccess(
        data.range,
        data.totalCount,
        Immutable.List(data.items.map((item: object) => Token.fromJS(item))),
        Immutable.List(data.countPerGroup.map(GroupAndCount.fromJS)),
        filter,
        advancedFilter,
        groupBy,
        sortOrder
      ),
      onError
  );
}

function generateNewToken (
  userId: string,
  tags: string,
  force: boolean,
  onSuccess: OnGenerateNewTokenSuccess,
  onError: OnError
): void {
  ConnectionSupport.post(
    'generateNewToken',
    {
      userId: userId,
      tags: tags,
      force: force
    },
    (data) => onSuccess(data.status, data.token),
    onError
  );
}

function revokeTokens (userIds: Immutable.List<string>, onSuccess: OnRevokeTokensSuccess, onError: OnError): void {
  ConnectionSupport.post(
    'revokeTokens',
    {ids: userIds},
    (data) => onSuccess(Immutable.List(data.ids)),
    onError
  );
}

function updateToken (
  userId: string,
  tags: string,
  onSuccess: OnUpdateTokenSuccess,
  onError: OnError
): void {
  ConnectionSupport.post(
    'updateToken',
    {
      userId: userId,
      tags: tags
    },
    onSuccess,
    onError
  );
}

const TokensConnection = {
  fetch,
  generateNewToken,
  revokeTokens,
  updateToken
};

export default TokensConnection;